import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { Row, Col, Card, Form, Button } from "react-bootstrap";
import { useAppContext } from "../../AppContext";
import orderRoute from "../../services/orderRoute";
import DatePicker from "react-datepicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";

const getMidnight = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0); // Set time to 00:00:00.000Z
  return d;
};

function getUTCMidnight(date) {
  const utc = new Date(date);
  utc.setUTCHours(0, 0, 0, 0);
  return utc;
}

export const AllOrdersChart = () => {
  const [orderOverviewData, setOrderOverviewData] = useState([]);

  const [loading, setLoading] = useState(false);
  const { showToast } = useAppContext();
  const [startDate, setStartDate] = useState(
    getUTCMidnight(new Date(new Date().setDate(new Date().getDate() - 6)))
  );

  const [endDate, setEndDate] = useState(new Date());
  const { getOrderOverviewByDate } = orderRoute();
  const [showDatePicker, setShowDatePicker] = useState(false);

  const fetchOrderOverview = () => {
    const isoStartDate = getUTCMidnight(startDate).toISOString();
    const isoEndDate = getUTCMidnight(endDate).toISOString();

    getOrderOverviewByDate(isoEndDate, isoStartDate, setLoading, setOrderOverviewData, showToast);
  };

  useEffect(() => {
    fetchOrderOverview();
  }, [startDate, endDate]);

  // Format the displayed date range
  const formatPeriod = () => {
    const options = { day: "2-digit", month: "short" };
    return `${startDate.toLocaleDateString("en-GB", { ...options, timeZone: "Africa/Lagos" })} - ${endDate.toLocaleDateString("en-GB", { ...options, timeZone: "Africa/Lagos" })}`;
  };

  return (
    <div>
      {/* All Orders Chart */}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className=" font-semibold">All Orders</h5>
        {/* Clickable Date Range Selector */}
        <div className="position-relative">
          <div
            className="px-3 py-2"
            style={{
              backgroundColor: "#F1F3F9",
              color: "#33333380",
              fontSize: "12px",
              cursor: "pointer",
            }}
            onClick={() => setShowDatePicker(!showDatePicker)}
          >
            {formatPeriod()} <FontAwesomeIcon icon={faChevronDown} />
          </div>

          {/* Date Picker Form (Visible on Click) */}
          {showDatePicker && (
            <div
              className="position-absolute bg-white px-3 pb-3 pt-1 shadow-sm rounded"
              style={{ right: 20, zIndex: 1000, width: "310px", height: "auto" }}
            >
              <div className="justify-content-end d-flex mb-2">
                <Button className="btn btn-danger btn-sm" onClick={() => setShowDatePicker(false)}>
                  X
                </Button>
              </div>
              <Form.Group className="mb-2">
                <Form.Label className="me-2">Start Date: </Form.Label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  className="form-control"
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="me-2">End Date: </Form.Label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  className="form-control"
                />
              </Form.Group>
            </div>
          )}
        </div>
      </div>
      {loading ? (
        <div
          className="justify-content-center align-items-center text-center"
          style={{ padding: "100px" }}
        >
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : orderOverviewData?.length > 0 ? (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={orderOverviewData} margin={{ top: 20, right: 20, bottom: 20, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis
              dataKey="period"
              tick={{ fontSize: 12 }}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={60}
            />

            <YAxis tickFormatter={(value) => value.toLocaleString()} tick={{ fontSize: 10 }} />

            <Tooltip formatter={(value) => `${value.toLocaleString()}`} />
            <Bar dataKey="totalOrders" fill="#4d6f48" />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="fw-medium px-5 py-5 ">
          <h4>You have no data</h4>
        </div>
      )}
    </div>
  );
};
export const TransactionOverview = () => {
  const [transactionOverviewData, setTransactionOverviewData] = useState([]);

  const [loading, setLoading] = useState(false);
  const { showToast } = useAppContext();
  const [startDate, setStartDate] = useState(
    getUTCMidnight(new Date(new Date().setDate(new Date().getDate() - 6)))
  );

  const [endDate, setEndDate] = useState(new Date());
  const { getTransactionsByDate } = orderRoute();
  const [showDatePicker2, setShowDatePicker2] = useState(false);

  const fetchTransactions = () => {
    const isoStartDate = getUTCMidnight(startDate).toISOString();
    const isoEndDate = getUTCMidnight(endDate).toISOString();

    getTransactionsByDate(
      isoEndDate,
      isoStartDate,
      setLoading,
      setTransactionOverviewData,
      showToast
    );
  };

  useEffect(() => {
    fetchTransactions();
  }, [startDate, endDate]);

  // Format the displayed date range
  const formatPeriod = () => {
    const options = { day: "2-digit", month: "short" };
    return `${startDate.toLocaleDateString("en-GB", { ...options, timeZone: "Africa/Lagos" })} - ${endDate.toLocaleDateString("en-GB", { ...options, timeZone: "Africa/Lagos" })}`;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className=" font-semibold">Transaction Overview</h5>
        {/* Clickable Date Range Selector */}
        <div className="postion-relative">
          <div
            className="px-3 py-2"
            style={{
              backgroundColor: "#F1F3F9",
              color: "#33333380",
              fontSize: "12px",
              cursor: "pointer",
            }}
            onClick={() => setShowDatePicker2(!showDatePicker2)}
          >
            {formatPeriod()} <FontAwesomeIcon icon={faChevronDown} />
          </div>

          {/* Date Picker Form (Visible on Click) */}
          {showDatePicker2 && (
            <div
              className="position-absolute bg-white px-3 pb-3 pt-1 shadow-sm rounded"
              style={{ right: 20, zIndex: 1000 }}
            >
              <div className="justify-content-end d-flex mb-2">
                <Button className="btn btn-danger btn-sm" onClick={() => setShowDatePicker2(false)}>
                  X
                </Button>
              </div>
              <Form.Group className="mb-2">
                <Form.Label className="me-2">Start Date: </Form.Label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  className="form-control"
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="me-2">End Date: </Form.Label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  className="form-control"
                />
              </Form.Group>
            </div>
          )}
        </div>
      </div>
      {loading ? (
        <div
          className="justify-content-center align-items-center text-center"
          style={{ padding: "100px" }}
        >
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : transactionOverviewData?.length > 0 ? (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            data={transactionOverviewData}
            margin={{ top: 20, right: 20, bottom: 20, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis
              dataKey="period"
              tick={{ fontSize: 12 }}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={60}
            />

            <YAxis tickFormatter={(value) => `N${value / 1000}k`} tick={{ fontSize: 10 }} />

            <Tooltip formatter={(value) => `N${value.toLocaleString()}`} />
            <Bar dataKey="totalTransactions" fill="#4d6f48" />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="fw-medium px-5 py-5 ">
          <h4>You have no data</h4>
        </div>
      )}
    </div>
  );
};

export const DashboardCharts = () => {
  return (
    <Row className="gap-4 my-4">
      {/* All Orders Chart */}
      <Col className="bg-white p-4" style={{ border: "1px solid #E1E6EF" }}>
        <AllOrdersChart />
      </Col>

      {/* Transaction Overview Chart */}
      <Col className="bg-white p-4" style={{ border: "1px solid #E1E6EF" }}>
        <TransactionOverview />
      </Col>
    </Row>
  );
};

export const AppVisitsOverTime = () => {
  const [tractionData, setTractionData] = useState(null);
  const { showToast } = useAppContext();
  const { getAppTractionByDate } = orderRoute();
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("last_12_hours");
  const [customRange, setCustomRange] = useState({ start: null, end: null });

  useEffect(() => {
    if (selectedPeriod !== "custom") {
      getAppTractionByDate(null, null, selectedPeriod, setLoading, setTractionData, showToast);
    }
  }, [selectedPeriod]);

  const handleCustomFetch = () => {
    if (customRange.start && customRange.end) {
      const isoStartDate = customRange.start.toISOString();
      const isoEndDate = customRange.end.toISOString();
      getAppTractionByDate(
        isoStartDate,
        isoEndDate,
        selectedPeriod,
        setLoading,
        setTractionData,
        showToast
      );
    }
  };

  const getPeriodTitle = () => {
    switch (selectedPeriod) {
      case "last_12_hours":
        return "Last 12 Hours Sales";
      case "last_24_hours":
        return "Last 24 Hours Sales";
      case "weekly":
        return "Weekly Sales";
      case "monthly":
        return "Monthly Sales";
      case "yearly":
        return "Yearly Sales";
      case "custom":
        if (customRange.start && customRange.end) {
          return `From ${customRange.start.toLocaleDateString("en-US", { timeZone: "Africa/Lagos" })} to ${customRange.end.toLocaleDateString("en-US", { timeZone: "Africa/Lagos" })}`;
        } else {
          return "Custom Date Range";
        }
      default:
        return "Sales Data";
    }
  };

  const chartData =
    tractionData?.visits_overtime?.map((item) => ({
      time: item.period,
      visits: item.total_visits,
      checkouts: item.successful_checkouts,
    })) || [];

  return (
    <Row className="gap-2 my-4">
      <Col className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          {/* <h5 className="font-semibold">App Visits / Checkouts Over Time</h5> */}
          <h5 className="font-semibold">Checkouts Over Time</h5>
          <Form.Select
            size="sm"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            style={{ width: "145px", border: "none" }}
          >
            <option value="last_12_hours">Last 12 hours</option>
            <option value="last_24_hours">Last 24 hours</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
            <option value="custom">Custom</option>
          </Form.Select>
        </div>

        {selectedPeriod === "custom" && (
          <div className="d-flex gap-2 align-items-center mb-3">
            <DatePicker
              selected={customRange.start}
              onChange={(date) => setCustomRange({ ...customRange, start: date })}
              selectsStart
              startDate={customRange.start}
              endDate={customRange.end}
              placeholderText="Start Date"
              className="form-control"
            />
            <DatePicker
              selected={customRange.end}
              onChange={(date) => setCustomRange({ ...customRange, end: date })}
              selectsEnd
              startDate={customRange.start}
              endDate={customRange.end}
              minDate={customRange.start}
              placeholderText="End Date"
              className="form-control"
            />
            <button onClick={handleCustomFetch} className="btn btn-sm btn-primary">
              Apply
            </button>
          </div>
        )}
        {loading ? (
          <div
            className="justify-content-center align-items-center text-center"
            style={{ padding: "100px" }}
          >
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : tractionData && Object.keys(tractionData).length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#597E47",
                  border: "none",
                  borderRadius: "8px",
                  color: "#fff",
                }}
                labelStyle={{ color: "#FFFFFF", fontWeight: "bold" }}
                itemStyle={{ color: "#ffffff" }}
              />

              <Legend layout="horizontal" verticalAlign="bottom" align="center" />

              {/* <Line
                type="monotone"
                dataKey="visits"
                stroke="#e88c3d"
                strokeWidth={2}
                dot={{ r: 5 }}
              /> */}
              <Line
                type="monotone"
                dataKey="checkouts"
                stroke="#4d6f48"
                strokeWidth={2}
                dot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="fw-medium px-5 py-5 ">
            <h4>You have no orders</h4>
          </div>
        )}
      </Col>

      <Col md={3}>
        <div className="pt-4">
          <h6 className=" pt-1 fw-bold">{getPeriodTitle()}</h6>
          {loading ? (
            <div
              className="justify-content-center align-items-center text-center"
              style={{ padding: "100px" }}
            >
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : tractionData && Object.keys(tractionData).length > 0 ? (
            <>
              <h5 className="fw-bold mb-0 mt-4">
                {tractionData?.last_sales?.itemsSold?.toLocaleString() || 0}
              </h5>
              <p style={{ color: "#5A607F" }}>Items Sold</p>

              <h5 className="fw-bold mt-3 mb-0">
                ₦{tractionData?.last_sales?.revenue.toLocaleString() || 0}
              </h5>
              <p style={{ color: "#5A607F" }}>In Revenue</p>

              <hr />
              <div className="mb-2">
                <h5 className="fw-bold mb-0">
                  {tractionData?.grouped_sales?.totalVisits?.toLocaleString() || 0}
                </h5>
                <p style={{ color: "#5A607F" }}>Total Visits</p>
              </div>

              <div>
                <h5 className="fw-bold mb-0">
                  {tractionData?.grouped_sales?.successfulCheckouts?.toLocaleString() || 0}
                </h5>
                <p style={{ color: "#5A607F" }}>Total Successful Checkouts</p>
              </div>
            </>
          ) : (
            <div className="fw-medium px-5 py-5 ">
              <h4>You have no data</h4>
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
};
